import { createSelector } from "@reduxjs/toolkit";
import { ConfigMap } from "../interfaces/configmap";
import { GroupBlockData } from "../interfaces/groupblock";
import {
  CategorizationLinkDto,
  CategorizationWithConfig,
  ICategorization,
} from "../interfaces/category";

export const selectConfigMapCategorizationLinks = createSelector(
  [(configMap: ConfigMap | undefined) => configMap],
  (configMap): CategorizationLinkDto[] => {
    if (!configMap) return [];

    const configMapLinks =
      configMap.categorizationLinks?.filter((link) => link.configMapId) ?? [];

    // Get links from GroupBlocks
    const groupBlockLinks =
      configMap.groupBlocks?.flatMap(
        (gb) =>
          gb.categorizationLinks?.filter((link) => link.groupBlockId) ?? [],
      ) ?? [];

    // Get links from EntityTypes
    const entityTypeLinks =
      configMap.groupBlocks?.flatMap(
        (gb) =>
          gb.groupBlockEntityTypes?.flatMap(
            (gbet) => gbet.entityType.categorizationLinks || [],
          ) ?? [],
      ) ?? [];

    return [...configMapLinks, ...groupBlockLinks, ...entityTypeLinks];
  },
);

export const selectConfigMapCategorizations = createSelector(
  [(configMap: ConfigMap | undefined) => configMap],
  (configMap): CategorizationWithConfig[] => {
    if (!configMap?.categorizationLinks) return [];

    return configMap.categorizationLinks
      .filter((link) => link.configMapId)
      .map((link) => ({
        id: link.id,
        categorization: link.categorization,
        required: link.required,
      }));
  },
);

export const selectGroupBlockCategorizations = createSelector(
  [(groupBlock: GroupBlockData | undefined) => groupBlock],
  (groupBlock): CategorizationWithConfig[] => {
    if (!groupBlock?.categorizationLinks) return [];

    return groupBlock.categorizationLinks
      .filter((link) => link.groupBlockId)
      .map((link) => ({
        id: link.id,
        categorization: link.categorization,
        required: link.required,
      }));
  },
);

export const selectEntityTypeCategorizations = createSelector(
  [
    (configMap: ConfigMap | undefined, entityTypeId?: string) => ({
      configMap,
      entityTypeId,
    }),
  ],
  ({ configMap, entityTypeId }): CategorizationWithConfig[] => {
    if (!configMap) return [];

    const entityTypeLinks: CategorizationWithConfig[] = [];

    for (const groupBlock of configMap.groupBlocks || []) {
      for (const gbet of groupBlock.groupBlockEntityTypes || []) {
        if (!entityTypeId || gbet.entityType.id === entityTypeId) {
          const links =
            gbet.entityType.categorizationLinks?.map((link) => ({
              id: link.id,
              categorization: link.categorization,
              required: link.required,
            })) ?? [];
          entityTypeLinks.push(...links);
        }
      }
    }

    return entityTypeLinks;
  },
);

export const selectAllConfigMapCategorizations = createSelector(
  [(configMap: ConfigMap | undefined) => configMap],
  (configMap): CategorizationWithConfig[] => {
    if (!configMap) return [];

    const configMapCategorizations = selectConfigMapCategorizations(configMap);

    const groupBlockCategorizations =
      configMap.groupBlocks?.flatMap((gb) =>
        selectGroupBlockCategorizations(gb),
      ) ?? [];

    const entityTypeCategorizations = selectEntityTypeCategorizations(
      configMap,
      undefined,
    );

    return [
      ...configMapCategorizations,
      ...groupBlockCategorizations,
      ...entityTypeCategorizations,
    ];
  },
);

export const selectAllCategorizationObjects = createSelector(
  [selectAllConfigMapCategorizations],
  (categorizationsWithConfig): ICategorization[] => {
    return categorizationsWithConfig.map((cwc) => cwc.categorization);
  },
);

export const selectCategorizationsByContext = createSelector(
  [
    (
      configMap: ConfigMap | undefined,
      context: "configMap" | "groupBlock" | "entityType",
      contextId?: string,
    ) => ({
      configMap,
      context,
      contextId,
    }),
  ],
  ({ configMap, context, contextId }): CategorizationWithConfig[] => {
    if (!configMap) {
      return [];
    }

    if (context === "configMap") {
      return selectConfigMapCategorizations(configMap);
    } else if (context === "groupBlock") {
      const groupBlock = contextId
        ? configMap.groupBlocks?.find((gb) => gb.id === contextId)
        : undefined;

      if (!groupBlock) {
        // If no specific groupBlock, get all groupBlock categorizations
        return (
          configMap.groupBlocks?.flatMap((gb) =>
            selectGroupBlockCategorizations(gb),
          ) ?? []
        );
      }

      return selectGroupBlockCategorizations(groupBlock);
    } else if (context === "entityType") {
      return selectEntityTypeCategorizations(configMap, contextId);
    }

    return [];
  },
);
